<script setup lang="ts">
    import type { StoryblokRichtext } from '~/.storyblok/types/storyblok';
    import type { FormPresentationNode, FrontendFieldDefinition, RichTextDocument } from '@hmmh/storyblok-forms/client';
    import type { ComputedRef } from 'vue';
    import type { FieldComponentResolver } from './fieldComponentResolver';

    const props = defineProps<{
        nodes: FormPresentationNode[];
        fields: FrontendFieldDefinition[];
        fieldComponentResolver: FieldComponentResolver;
        labelPosition?: 'inside' | 'outside' | '';
    }>();

    defineOptions({ name: 'FormPresentation' });

    const activeValues = inject<ComputedRef<Record<string, unknown>>>('formActiveValues');

    const fieldsByName = computed(() => new Map(props.fields.map((field) => [field.name, field])));
    const asStoryblokRichtext = (document: RichTextDocument): StoryblokRichtext =>
        document as unknown as StoryblokRichtext;
</script>

<template>
    <template
        v-for="(node, index) in nodes"
        :key="node.kind === 'field' || node.kind === 'conditional' ? node.name : `${node.kind}-${index}`"
    >
        <template v-if="node.kind === 'field'">
            <component
                :is="fieldComponentResolver.resolve(fieldsByName.get(node.name)!.type)"
                v-if="fieldsByName.get(node.name) && fieldComponentResolver.resolve(fieldsByName.get(node.name)!.type)"
                v-editable="node.blok"
                :field="fieldsByName.get(node.name)"
            />
            <p v-else-if="fieldsByName.get(node.name)">Unknown field type: {{ fieldsByName.get(node.name)!.type }}</p>
        </template>

        <div
            v-else-if="node.kind === 'conditional' && fieldsByName.has(node.name)"
            v-editable="node.blok"
            class="form-presentation__conditional"
        >
            <component
                :is="fieldComponentResolver.resolve('conditional')"
                :field="fieldsByName.get(node.name)"
            />
            <template
                v-for="option in node.options"
                :key="option.key"
            >
                <div
                    v-if="activeValues?.[node.name] === option.key"
                    v-editable="option.blok"
                    class="form-presentation__branch"
                >
                    <FormPresentation
                        :nodes="option.children"
                        :fields="fields"
                        :field-component-resolver="fieldComponentResolver"
                        :label-position="labelPosition"
                    />
                </div>
            </template>
        </div>

        <fieldset
            v-else-if="node.kind === 'fieldset'"
            v-editable="node.blok"
            class="form-presentation__fieldset"
        >
            <legend
                v-if="node.legend"
                :class="{ '--labels-inside': props.labelPosition === 'inside' }"
            >
                {{ node.legend }}
            </legend>
            <FormPresentation
                :nodes="node.children"
                :fields="fields"
                :field-component-resolver="fieldComponentResolver"
                :label-position="labelPosition"
            />
        </fieldset>

        <div
            v-else-if="node.kind === 'static-text'"
            v-editable="node.blok"
            class="text form-presentation__static-text"
        >
            <RichText :doc="asStoryblokRichtext(node.bodytext)" />
        </div>

        <div
            v-else-if="node.kind === 'row'"
            v-editable="node.blok"
            class="form-presentation__row"
            :class="node.layout ? `form-presentation__row--${node.layout}` : undefined"
        >
            <FormPresentation
                :nodes="node.children"
                :fields="fields"
                :field-component-resolver="fieldComponentResolver"
                :label-position="labelPosition"
            />
        </div>
    </template>
</template>

<style scoped lang="scss">
    @import '~/assets/scss/text/text.scss';

    .form-presentation__conditional,
    .form-presentation__branch {
        display: flex;
        flex-direction: column;
        gap: var(--spacing);
        min-width: 0;
    }

    .form-presentation__fieldset {
        border: 0;
        display: flex;
        flex-direction: column;
        gap: var(--spacing);
        margin: 0;
        min-width: 0;
        padding: 0;

        legend.--labels-inside {
            margin-bottom: var(--spacing);
        }
    }

    .form-presentation__row {
        display: grid;
        gap: var(--spacing);
        grid-template-columns: minmax(0, 1fr);

        @media (min-width: $br-m) {
            &--1 {
                grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            }

            &--2 {
                grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
            }

            &--3 {
                grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
            }
        }
    }
</style>
